
import { useEffect } from 'react'

import { useLocation, matchPath } from 'react-router-dom'

import { trackPageView } from 'services/analytics/googleAnalytics'
import { useAuthStore } from 'store'

type PageInfo = {
  path: string
  type: string
  title: string
}

type RouteInfo = {
  pattern: string
  page: PageInfo
}

/**
 * Exact application pages.
 *
 * Creator profiles (/:username) are intentionally
 * excluded. They are tracked after Profile.tsx
 * confirms the creator exists.
 */
const PAGES: Record<string, PageInfo> = {
  '/': {
    path: '/',
    type: 'home',
    title: 'Home | AllFans'
  },
  '/following': {
    path: '/following',
    type: 'following_feed',
    title: 'Following | AllFans'
  },
  '/creators': {
    path: '/creators',
    type: 'creator_discovery',
    title: 'Creators | AllFans'
  },
  '/subscriptions': {
    path: '/subscriptions',
    type: 'subscriptions',
    title: 'Subscriptions | AllFans'
  },
  '/subscriptions/lists': {
    path: '/subscriptions/lists',
    type: 'subscription_lists',
    title: 'Subscription Lists | AllFans'
  },
  '/chat': {
    path: '/chat',
    type: 'messaging',
    title: 'Messages | AllFans'
  },
  '/fans': {
    path: '/fans',
    type: 'fans',
    title: 'Fans | AllFans'
  },
  '/friends': {
    path: '/friends',
    type: 'friends',
    title: 'Friends | AllFans'
  },
  '/streams': {
    path: '/streams',
    type: 'streams',
    title: 'Streams | AllFans'
  },
  '/vault': {
    path: '/vault',
    type: 'vault',
    title: 'Vault | AllFans'
  },
  '/media': {
    path: '/media',
    type: 'media',
    title: 'Media | AllFans'
  },
  '/media_categories': {
    path: '/media_categories',
    type: 'media_categories',
    title: 'Media Categories | AllFans'
  },
  '/playlists': {
    path: '/playlists',
    type: 'playlists',
    title: 'Playlists | AllFans'
  },
  '/notifications': {
    path: '/notifications',
    type: 'notifications',
    title: 'Notifications | AllFans'
  },
  '/purchases': {
    path: '/purchases',
    type: 'purchases',
    title: 'Purchases | AllFans'
  },
  '/transactions': {
    path: '/transactions',
    type: 'transactions',
    title: 'Transactions | AllFans'
  },
  '/likes': {
    path: '/likes',
    type: 'likes',
    title: 'Likes | AllFans'
  },
  '/schedule': {
    path: '/schedule',
    type: 'schedule',
    title: 'Schedule | AllFans'
  },
  '/live': {
    path: '/live',
    type: 'live',
    title: 'Live | AllFans'
  },
  '/watch-history': {
    path: '/watch-history',
    type: 'watch_history',
    title: 'Watch History | AllFans'
  },
  '/discover/reels': {
    path: '/discover/reels',
    type: 'reels',
    title: 'Reels | AllFans'
  },
  '/new/post/create': {
    path: '/new/post/create',
    type: 'post_creation',
    title: 'Create Post | AllFans'
  },
  '/new/story': {
    path: '/new/story',
    type: 'story_creation',
    title: 'Create Story | AllFans'
  },
  '/new/reel': {
    path: '/new/reel',
    type: 'reel_creation',
    title: 'Create Reel | AllFans'
  },
  '/messages/new': {
    path: '/messages/new',
    type: 'messaging',
    title: 'New Message | AllFans'
  },
  '/become-a-creator': {
    path: '/become-a-creator',
    type: 'creator_onboarding',
    title: 'Become a Creator | AllFans'
  },
  '/menu': {
    path: '/menu',
    type: 'menu',
    title: 'Menu | AllFans'
  },
  '/termsandpolicies': {
    path: '/termsandpolicies',
    type: 'legal',
    title: 'Terms and Policies | AllFans'
  },
  '/termsandpolicies/tos': {
    path: '/termsandpolicies/tos',
    type: 'legal',
    title: 'Terms of Service | AllFans'
  },

  // Public informational and legal pages
  '/terms': {
    path: '/terms',
    type: 'legal',
    title: 'Terms | AllFans'
  },
  '/dmca': {
    path: '/dmca',
    type: 'legal',
    title: 'DMCA | AllFans'
  },
  '/privacypolicy': {
    path: '/privacypolicy',
    type: 'legal',
    title: 'Privacy Policy | AllFans'
  },
  '/acceptableuse': {
    path: '/acceptableuse',
    type: 'legal',
    title: 'Acceptable Use | AllFans'
  },
  '/usc': {
    path: '/usc',
    type: 'legal',
    title: 'USC | AllFans'
  },
  '/referralterms': {
    path: '/referralterms',
    type: 'legal',
    title: 'Referral Terms | AllFans'
  },
  '/cookiepolicy': {
    path: '/cookiepolicy',
    type: 'legal',
    title: 'Cookie Policy | AllFans'
  },
  '/complaintspolicy': {
    path: '/complaintspolicy',
    type: 'legal',
    title: 'Complaints Policy | AllFans'
  },
  '/fancreatorcontract': {
    path: '/fancreatorcontract',
    type: 'legal',
    title: 'Fan Creator Contract | AllFans'
  },
  '/creatorterms': {
    path: '/creatorterms',
    type: 'legal',
    title: 'Creator Terms | AllFans'
  },
  '/ccpapolicy': {
    path: '/ccpapolicy',
    type: 'legal',
    title: 'CCPA Policy | AllFans'
  },
  '/cookietable': {
    path: '/cookietable',
    type: 'legal',
    title: 'Cookie Table | AllFans'
  },
  '/biometricnotice': {
    path: '/biometricnotice',
    type: 'legal',
    title: 'Biometric Notice | AllFans'
  },
  '/contentmoderationpolicy': {
    path: '/contentmoderationpolicy',
    type: 'legal',
    title: 'Content Moderation | AllFans'
  }
}

/**
 * Dynamic application routes.
 *
 * Each pattern matches the real application URL,
 * but sends a sanitized path to Google Analytics.
 */
const DYNAMIC_ROUTES: RouteInfo[] = [
  {
    pattern: '/chat/:id',
    page: {
      path: '/chat',
      type: 'messaging',
      title: 'Messages | AllFans'
    }
  },
  {
    pattern: '/subscriptions/:id',
    page: {
      path: '/subscriptions',
      type: 'subscriptions',
      title: 'Subscriptions | AllFans'
    }
  },
  {
    pattern: '/fans/:slug',
    page: {
      path: '/fans',
      type: 'fans',
      title: 'Fans | AllFans'
    }
  },
  {
    pattern: '/vault/:id',
    page: {
      path: '/vault',
      type: 'vault',
      title: 'Vault | AllFans'
    }
  },
  {
    pattern: '/playlists/:id',
    page: {
      path: '/playlists',
      type: 'playlists',
      title: 'Playlists | AllFans'
    }
  },
  {
    pattern: '/analytics/:id',
    page: {
      path: '/analytics',
      type: 'creator_analytics',
      title: 'Creator Analytics | AllFans'
    }
  },
  {
    pattern: '/earnings/:id',
    page: {
      path: '/earnings',
      type: 'earnings',
      title: 'Earnings | AllFans'
    }
  },
  {
    pattern: '/referrals/:id',
    page: {
      path: '/referrals',
      type: 'referrals',
      title: 'Referrals | AllFans'
    }
  },
  {
    pattern: '/user-activity/:id',
    page: {
      path: '/user-activity',
      type: 'user_activity',
      title: 'User Activity | AllFans'
    }
  },
  {
    pattern: '/watch/:username',
    page: {
      path: '/watch',
      type: 'watch',
      title: 'Watch | AllFans'
    }
  },
  {
    pattern: '/model/:modelId/friends',
    page: {
      path: '/model/friends',
      type: 'public_friends',
      title: 'Public Friends | AllFans'
    }
  },
  {
    pattern: '/post/:id',
    page: {
      path: '/post',
      type: 'post',
      title: 'Post | AllFans'
    }
  },
  {
    pattern: '/edit/post/:id',
    page: {
      path: '/edit/post',
      type: 'post_editing',
      title: 'Edit Post | AllFans'
    }
  },
  {
    pattern: '/discover/reels/:reelId/profile',
    page: {
      path: '/discover/reels',
      type: 'reels',
      title: 'Reels | AllFans'
    }
  },
  {
    pattern: '/discover/reels/:reelId',
    page: {
      path: '/discover/reels',
      type: 'reels',
      title: 'Reels | AllFans'
    }
  },
  {
    pattern: '/how-to/tutorials/:id',
    page: {
      path: '/how-to',
      type: 'help',
      title: 'Tutorial | AllFans'
    }
  },
  {
    pattern: '/how-to/faq/:id',
    page: {
      path: '/how-to',
      type: 'help',
      title: 'FAQ | AllFans'
    }
  },
  {
    pattern: '/how-to/:id?',
    page: {
      path: '/how-to',
      type: 'help',
      title: 'Help | AllFans'
    }
  },
  {
    pattern: '/support/:id',
    page: {
      path: '/support',
      type: 'support',
      title: 'Support | AllFans'
    }
  }
]

/**
 * Authenticated routes.
 *
 * Routes not listed here may be tracked for guests.
 * This is an analytics filter, not authorization.
 */
const PROTECTED_PAGES = new Set([
  '/following',
  '/creators',
  '/subscriptions',
  '/subscriptions/lists',
  '/chat',
  '/fans',
  '/friends',
  '/streams',
  '/vault',
  '/media',
  '/media_categories',
  '/playlists',
  '/notifications',
  '/purchases',
  '/transactions',
  '/likes',
  '/schedule',
  '/live',
  '/watch-history',
  '/discover/reels',
  '/new/post/create',
  '/new/story',
  '/new/reel',
  '/messages/new',
  '/become-a-creator',
  '/menu',
  '/analytics',
  '/earnings',
  '/referrals',
  '/user-activity',
  '/watch',
  '/post',
  '/edit/post',
  '/support',
  '/how-to'
])

/**
 * Routes where we require a creator role.
 *
 * This avoids counting unauthorized visits
 * before ProtectedRoute redirects.
 */
const CREATOR_ONLY_PAGES = new Set([
  '/fans',
  '/friends',
  '/streams',
  '/vault',
  '/media_categories',
  '/schedule',
  '/analytics',
  '/earnings',
  '/referrals',
  '/user-activity',
  '/new/post/create',
  '/new/story',
  '/new/reel',
  '/edit/post'
])

/**
 * Routes excluded entirely from Google Analytics.
 *
 * These include temporary tokens, callbacks,
 * sensitive account workflows, and error pages.
 */
const EXCLUDED_PREFIXES = [
  '/email/verify',
  '/trial-link',
  '/google-callback',
  '/twitter-callback',
  '/vmc/identity-verification',
  '/reset-password',
  '/support/ticket',
  '/404'
]

const normalizePath = (pathname: string): string => {
  return pathname.replace(/\/+$/, '') || '/'
}

const resolvePage = (pathname: string): PageInfo | null => {
  const normalized = normalizePath(pathname)
  const lower = normalized.toLowerCase()

  if (
    EXCLUDED_PREFIXES.some(
      prefix =>
        lower === prefix ||
        lower.startsWith(`${prefix}/`)
    )
  ) {
    return null
  }

  // Match exact routes first.
  const exactPage = PAGES[lower]

  if (exactPage) {
    return exactPage
  }

  // Then check known dynamic routes.
  for (const route of DYNAMIC_ROUTES) {
    if (
      matchPath(
        {
          path: route.pattern,
          end: true,
          caseSensitive: false
        },
        normalized
      )
    ) {
      return route.page
    }
  }

  // Unknown URLs and creator profile URLs
  // are not handled by this component.
  return null
}

export const GoogleAnalyticsTracker = () => {
  const location = useLocation()

  useEffect(() => {
    const page = resolvePage(location.pathname)

    if (!page) return

    const { accessToken, userProfile } =
      useAuthStore.getState()

    const role = userProfile?.role?.toLowerCase()

    // Don't track protected pages for guests.
    if (PROTECTED_PAGES.has(page.path) && !accessToken) {
      return
    }

    // Wait until the authenticated role is loaded.
    if (
      PROTECTED_PAGES.has(page.path) &&
      !role
    ) {
      return
    }

    // Only permitted roles should count.
    if (
      PROTECTED_PAGES.has(page.path) &&
      role !== 'fan' &&
      role !== 'model'
    ) {
      return
    }

    // Creator-only pages should not count for fans.
    if (
      CREATOR_ONLY_PAGES.has(page.path) &&
      role !== 'model'
    ) {
      return
    }

    trackPageView({
      path: page.path,
      pageType: page.type,
      title: page.title,
      visitorStatus: accessToken
        ? 'authenticated'
        : 'guest',
      navigationKey: location.key
    })
  }, [location.key, location.pathname])

  return null
}
